if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        this.hoverMql = window.matchMedia('(min-width: 990px) and (hover: hover) and (pointer: fine)');
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          const thumbnailButton = mediaToSwitch.querySelector('button');
          if (!thumbnailButton) return;

          thumbnailButton.addEventListener('click', () => {
            this.setActiveMedia(mediaToSwitch.dataset.target, false);
            this.resetImageAutoplay();
          });

          // Desktop: hovering any supported gallery thumbnail (image or video)
          // swaps the main media. Touch/mobile keeps the original click behavior.
          if (['image', 'video', 'external_video'].includes(mediaToSwitch.dataset.mediaType)) {
            thumbnailButton.addEventListener('pointerenter', () => {
              if (!this.hoverMql.matches) return;
              this.setActiveMedia(mediaToSwitch.dataset.target, false);
              this.resetImageAutoplay();
            });
          }
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();

        // v102: troca automática de toda a mídia visual do produto a cada 10 segundos,
        // incluindo imagens e vídeos. Modelos 3D continuam fora do ciclo automático.
        this.imageAutoplayDelay = 10000;
        this.imageAutoplayTimer = null;
        this.imageMediaItems = Array.from(
          this.elements.thumbnails.querySelectorAll(
            '[data-media-type="image"][data-target], [data-media-type="video"][data-target], [data-media-type="external_video"][data-target]'
          )
        );
        this.videoPlaybackActive = false;
        this.videoPlaybackSlide = null;
        // v104: pausa o ciclo automático enquanto o visitante está analisando
        // uma imagem com o zoom por hover ativo.
        this.zoomInteractionActive = false;
        this.setupVideoPlaybackControls();
        this.onGalleryVisibilityChange = () => {
          if (document.hidden) {
            window.clearTimeout(this.imageAutoplayTimer);
            return;
          }
          this.scheduleImageAutoplay();
        };
        document.addEventListener('visibilitychange', this.onGalleryVisibilityChange);
        this.setupCustomThumbnailScroll();
        this.scheduleImageAutoplay();
      }

      setupCustomThumbnailScroll() {
        const control = this.elements.thumbnails?.querySelector('[data-gallery-scroll-control]');
        const scroller = this.elements.thumbnails?.querySelector('[id^="Slider-Thumbnails-"]');
        if (!control || !scroller) return;

        const track = control.querySelector('[data-gallery-scroll-track]');
        const thumb = control.querySelector('[data-gallery-scroll-thumb]');
        const previousButton = control.querySelector('[data-gallery-scroll-prev]');
        const nextButton = control.querySelector('[data-gallery-scroll-next]');
        if (!track || !thumb || !previousButton || !nextButton) return;

        this.thumbnailScroller = scroller;
        this.thumbnailScrollControl = control;

        let frame = 0;
        const update = () => {
          frame = 0;
          const maxScroll = Math.max(0, scroller.scrollHeight - scroller.clientHeight);
          const travel = Math.max(0, track.clientHeight - thumb.offsetHeight);
          const progress = maxScroll > 0 ? Math.min(1, Math.max(0, scroller.scrollTop / maxScroll)) : 0;

          thumb.style.top = `${progress * travel}px`;
          control.classList.toggle('is-hidden', maxScroll <= 1);
          previousButton.disabled = maxScroll <= 1 || scroller.scrollTop <= 1;
          nextButton.disabled = maxScroll <= 1 || scroller.scrollTop >= maxScroll - 1;
        };

        const requestUpdate = () => {
          if (frame) return;
          frame = window.requestAnimationFrame(update);
        };

        const scrollStep = () => Math.max(82, scroller.clientHeight * 0.42);

        previousButton.addEventListener('click', () => {
          scroller.scrollBy({ top: -scrollStep(), behavior: 'smooth' });
        });

        nextButton.addEventListener('click', () => {
          scroller.scrollBy({ top: scrollStep(), behavior: 'smooth' });
        });

        track.addEventListener('pointerdown', (event) => {
          if (event.target === thumb) return;
          const maxScroll = Math.max(0, scroller.scrollHeight - scroller.clientHeight);
          if (!maxScroll) return;

          const rect = track.getBoundingClientRect();
          const travel = Math.max(1, track.clientHeight - thumb.offsetHeight);
          const y = Math.min(travel, Math.max(0, event.clientY - rect.top - thumb.offsetHeight / 2));
          scroller.scrollTo({ top: (y / travel) * maxScroll, behavior: 'smooth' });
        });

        let dragStartY = 0;
        let dragStartScroll = 0;
        thumb.addEventListener('pointerdown', (event) => {
          if (event.button !== undefined && event.button !== 0) return;
          dragStartY = event.clientY;
          dragStartScroll = scroller.scrollTop;
          thumb.setPointerCapture?.(event.pointerId);
          event.preventDefault();
        });

        thumb.addEventListener('pointermove', (event) => {
          if (!thumb.hasPointerCapture?.(event.pointerId)) return;
          const maxScroll = Math.max(0, scroller.scrollHeight - scroller.clientHeight);
          const travel = Math.max(1, track.clientHeight - thumb.offsetHeight);
          scroller.scrollTop = Math.min(maxScroll, Math.max(0, dragStartScroll + ((event.clientY - dragStartY) / travel) * maxScroll));
        });

        const releaseThumb = (event) => {
          if (thumb.hasPointerCapture?.(event.pointerId)) thumb.releasePointerCapture(event.pointerId);
        };
        thumb.addEventListener('pointerup', releaseThumb);
        thumb.addEventListener('pointercancel', releaseThumb);

        scroller.addEventListener('scroll', requestUpdate, { passive: true });

        if ('ResizeObserver' in window) {
          this.thumbnailScrollResizeObserver = new ResizeObserver(requestUpdate);
          this.thumbnailScrollResizeObserver.observe(scroller);
          this.thumbnailScrollResizeObserver.observe(track);
        } else {
          window.addEventListener('resize', requestUpdate, { passive: true });
        }

        requestUpdate();
        window.setTimeout(requestUpdate, 100);
      }

      setupVideoPlaybackControls() {
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((slide) => {
          const deferredMedia = slide.querySelector('.media-type-video deferred-media, .media-type-external_video deferred-media');
          if (!deferredMedia) return;

          const poster = deferredMedia.querySelector('.deferred-media__poster');
          if (!poster) return;

          // O vídeo só começa quando o visitante clica no PLAY. Enquanto ele toca,
          // o ciclo automático da galeria fica totalmente pausado.
          poster.addEventListener(
            'click',
            () => {
              this.videoPlaybackActive = true;
              this.videoPlaybackSlide = slide;
              window.clearTimeout(this.imageAutoplayTimer);

              // O conteúdo do <deferred-media> é inserido pelo componente nativo
              // do Shopify no mesmo clique. Esperamos um tick para conectar o evento ended.
              window.setTimeout(() => this.bindVideoLifecycle(slide, deferredMedia), 0);
            },
            { capture: true }
          );
        });
      }

      bindVideoLifecycle(slide, deferredMedia) {
        if (this.videoPlaybackSlide !== slide) return;

        const video = deferredMedia.querySelector('video');
        if (video) {
          // O slideshow precisa esperar o vídeo terminar; portanto o loop não pode
          // impedir o evento ended nesta reprodução iniciada pelo usuário.
          video.loop = false;
          video.removeAttribute('loop');

          if (!video.dataset.charmosoGalleryBound) {
            video.dataset.charmosoGalleryBound = 'true';

            video.addEventListener('ended', () => {
              if (this.videoPlaybackSlide !== slide) return;
              this.videoPlaybackActive = false;
              this.videoPlaybackSlide = null;

              // Ao terminar, avança para a próxima mídia e reinicia o ciclo de 10s.
              this.showNextImage();
              this.scheduleImageAutoplay();
            });
          }

          // Em alguns navegadores o autoplay do template pode não disparar imediatamente.
          // Como este método só é chamado após um clique explícito no PLAY, play() é permitido.
          const playPromise = video.play?.();
          if (playPromise?.catch) playPromise.catch(() => {});
          return;
        }

        // Vídeo externo: mantém o ciclo pausado enquanto essa mídia estiver aberta.
        // Ao trocar de miniatura, setActiveMedia libera o ciclo normalmente.
        const iframe = deferredMedia.querySelector('iframe');
        if (iframe) this.videoPlaybackActive = true;
      }

      resetVideoMedia(slide) {
        if (!slide) return;
        const deferredMedia = slide.querySelector('.media-type-video deferred-media, .media-type-external_video deferred-media');
        if (!deferredMedia) return;

        const loadedMedia = deferredMedia.querySelector('video, iframe');
        if (loadedMedia?.nodeName === 'VIDEO') {
          try {
            loadedMedia.pause();
            loadedMedia.currentTime = 0;
          } catch (error) {
            // Nada a fazer: o elemento será removido logo abaixo.
          }
        }

        // Volta ao pôster original para que o botão PLAY apareça novamente
        // quando o ciclo chegar a esse vídeo numa próxima vez.
        loadedMedia?.remove();
        deferredMedia.removeAttribute('loaded');
      }

      pauseImageAutoplayForZoom() {
        if (!this.hoverMql.matches) return;
        this.zoomInteractionActive = true;
        window.clearTimeout(this.imageAutoplayTimer);
      }

      resumeImageAutoplayAfterZoom() {
        if (!this.zoomInteractionActive) return;
        this.zoomInteractionActive = false;
        // Ao sair do zoom, começa uma nova contagem completa de 10 segundos.
        this.scheduleImageAutoplay();
      }

      scheduleImageAutoplay() {
        window.clearTimeout(this.imageAutoplayTimer);
        if (
          document.hidden ||
          this.videoPlaybackActive ||
          this.zoomInteractionActive ||
          this.imageMediaItems?.length < 2
        ) return;

        this.imageAutoplayTimer = window.setTimeout(() => {
          this.showNextImage();
          this.scheduleImageAutoplay();
        }, this.imageAutoplayDelay);
      }

      resetImageAutoplay() {
        this.scheduleImageAutoplay();
      }

      showNextImage() {
        if (!this.imageMediaItems?.length) return;

        // Primeiro identifica a imagem ativa pelo thumbnail; se algum script do
        // Shopify tiver recriado o estado, usa a mídia principal ativa como fallback.
        let currentIndex = this.imageMediaItems.findIndex((item) =>
          item.querySelector('button[aria-current="true"]')
        );

        if (currentIndex < 0) {
          const activeMedia = this.elements.viewer.querySelector('[data-media-id].is-active');
          const activeMediaId = activeMedia?.dataset?.mediaId;
          if (activeMediaId) {
            currentIndex = this.imageMediaItems.findIndex((item) => item.dataset.target === activeMediaId);
          }
        }

        const nextIndex = currentIndex < 0 ? 0 : (currentIndex + 1) % this.imageMediaItems.length;
        const nextItem = this.imageMediaItems[nextIndex];
        if (!nextItem?.dataset?.target) return;

        this.setActiveMedia(nextItem.dataset.target, false);
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }

        const previousActiveMedia = this.elements.viewer.querySelector('[data-media-id].is-active');
        if (previousActiveMedia && previousActiveMedia !== activeMedia) {
          if (this.videoPlaybackSlide === previousActiveMedia) {
            this.videoPlaybackActive = false;
            this.videoPlaybackSlide = null;
          }
          this.resetVideoMedia(previousActiveMedia);
        }

        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        // No desktop as miniaturas são verticais; no mobile permanecem horizontais.
        if (this.dataset.desktopLayout.includes('thumbnail') && this.hoverMql.matches) {
          this.elements.thumbnails.slider.scrollTo({ top: thumbnail.offsetTop, behavior: 'smooth' });
        } else {
          this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft, behavior: 'smooth' });
        }
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (!deferredMedia) return;

        // Imagens não usam deferred-media. Modelos 3D mantêm o comportamento nativo.
        // Vídeos ficam no pôster com o PLAY visível e só começam após o clique do usuário.
        const isVideo = activeItem.querySelector('.media-type-video, .media-type-external_video');
        if (isVideo) return;

        deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }

      disconnectedCallback() {
        window.clearTimeout(this.imageAutoplayTimer);
        if (this.onGalleryVisibilityChange) {
          document.removeEventListener('visibilitychange', this.onGalleryVisibilityChange);
        }
        this.thumbnailScrollResizeObserver?.disconnect();
      }
    }
  );
}
